import { Component, OnInit } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import Swal from 'sweetalert2';
import { AuthService } from '../../../core/auth.service';
import { StaffService } from '../staff.service';

@Component({
  selector: 'app-staff-overview',
  templateUrl: './staff-overview.component.html',
  styleUrls: ['./staff-overview.component.scss']
})
export class StaffOverviewComponent implements OnInit {
  loading = false;
  staff: any[] = [];
  pendingRequests: any[] = [];
  inbox: any[] = [];

  constructor(
    private staffService: StaffService,
    private auth: AuthService
  ) {}

  ngOnInit(): void {
    this.loadOverview();
  }

  loadOverview(): void {
    this.loading = true;
    forkJoin({
      staff: this.canViewStaffDirectory() ? this.staffService.getAll() : of([]),
      pendingRequests: this.canViewApprovalQueue() ? this.staffService.getAllRequests({ status: 'Pending' }) : of([]),
      inbox: this.canViewMessageInbox() ? this.staffService.getInboxMessages() : of([])
    }).subscribe({
      next: ({ staff, pendingRequests, inbox }) => {
        this.staff = staff || [];
        this.pendingRequests = pendingRequests || [];
        this.inbox = inbox || [];
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load staff overview.', 'error');
      }
    });
  }

  get activeStaffCount(): number {
    return this.staff.filter((row) => row?.isActive).length;
  }

  get inactiveStaffCount(): number {
    return Math.max(0, this.staff.length - this.activeStaffCount);
  }

  get unreadInboxCount(): number {
    return this.inbox.filter((row) => !row?.isRead).length;
  }

  get departmentBreakdown(): Array<{ label: string; value: number }> {
    const map = new Map<string, number>();
    this.staff.forEach((row) => {
      const key = String(row?.department || 'Unassigned').trim() || 'Unassigned';
      map.set(key, (map.get(key) || 0) + 1);
    });

    return Array.from(map.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }

  get positionBreakdown(): Array<{ label: string; value: number }> {
    const map = new Map<string, number>();
    this.staff.forEach((row) => {
      const key = String(row?.position || 'No Position').trim() || 'No Position';
      map.set(key, (map.get(key) || 0) + 1);
    });

    return Array.from(map.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }

  get recentRequests(): any[] {
    return [...this.pendingRequests]
      .sort((a, b) => new Date(b.requestedAt || b.createdAt || 0).getTime() - new Date(a.requestedAt || a.createdAt || 0).getTime())
      .slice(0, 6);
  }

  get spotlightStaff(): any[] {
    return [...this.staff]
      .sort((a, b) => String(a?.fullName || '').localeCompare(String(b?.fullName || '')))
      .slice(0, 6);
  }

  private canViewStaffDirectory(): boolean {
    return this.auth.hasAnyPermission(['staff.view', 'staff.manage']);
  }

  private canViewApprovalQueue(): boolean {
    return this.auth.hasAnyPermission(['staff.request.view', 'staff.request.approve', 'staff.manage']);
  }

  private canViewMessageInbox(): boolean {
    return this.auth.hasAnyPermission(['staff.message.view', 'staff.message.send', 'staff.manage']);
  }
}
